import { useForm } from '@tanstack/react-form'
import type { ProductStage } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { FormField } from '@workspace/ui/components/form-field'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { ToggleField } from '@workspace/ui/components/toggle-field'
import { formatDate, formatFileSize } from '@workspace/ui/lib/utils'
import { Check, Eye, EyeOff, FileText, Plus, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DocumentUpload } from '../../../components/document-upload'
import {
  useCreateStartupMemberMutation,
  useDeleteStartupMemberMutation,
  useLinkStartupDocumentMutation,
  useStartupConfigQuery,
  useStartupDocumentsQuery,
  useStartupMembersQuery,
  useStartupQuery,
  useUnlinkStartupDocumentMutation,
  useUpdateStartupDocumentMutation,
  useUpdateStartupMemberMutation,
  useUpdateStartupMutation,
} from '../../../hooks/api'
import { useImageUpload } from '../../../hooks/use-image-upload'
import { DocumentPreviewModal, type PreviewDocument } from '../../preview'
import type { DocumentLabelEdit, PendingDocument } from '../types/startup.types'
import { AddMemberForm } from './add-member-form'
import { MemberCard } from './member-card'

type StartupEditProps = {
  onExitEdit: () => void
}

export function StartupEdit({ onExitEdit }: StartupEditProps) {
  const { data: startupResponse, isLoading, error } = useStartupQuery()
  const { data: configResponse } = useStartupConfigQuery()
  const startup = startupResponse?.data
  const startupId = startup?.id

  const { data: documentsData } = useStartupDocumentsQuery(startupId)
  const documents = documentsData?.data ?? []

  const { data: membersData } = useStartupMembersQuery(startupId)
  const members = membersData?.data ?? []

  const createMemberMutation = useCreateStartupMemberMutation()
  const updateMemberMutation = useUpdateStartupMemberMutation()
  const deleteMemberMutation = useDeleteStartupMemberMutation()

  const categories = configResponse?.data?.categories ?? []
  const productStages = configResponse?.data?.productStages ?? []
  const raiseStages = configResponse?.data?.raiseStages ?? []

  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([])
  const [documentLabelEdits, setDocumentLabelEdits] = useState<DocumentLabelEdit[]>([])
  const [documentsToUnlink, setDocumentsToUnlink] = useState<string[]>([])
  const [editedTags, setEditedTags] = useState<string[]>([])
  const [newTagInput, setNewTagInput] = useState('')
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null)

  const updateStartupMut = useUpdateStartupMutation()
  const unlinkDocumentMut = useUnlinkStartupDocumentMutation()
  const linkDocumentMut = useLinkStartupDocumentMutation()
  const updateDocumentMut = useUpdateStartupDocumentMutation()

  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)

  const logoUpload = useImageUpload({
    onSuccess: (url) => setLogoUrl(url),
  })
  const coverUpload = useImageUpload({
    onSuccess: (url) => setCoverImageUrl(url),
  })

  const getFormValues = (data?: typeof startup) => ({
    name: data?.name ?? '',
    categoryId: data?.categoryId ?? '',
    productStage: (data?.productStage as ProductStage | '') ?? '',
    isFundraising: data?.isFundraising ?? false,
    fundraisingAmount: data?.fundraisingAmount ?? '',
    fundraisingStage: data?.fundraisingStage ?? '',
    isStealth: data?.isStealth ?? false,
    northStarMetricName: data?.northStarMetricName ?? '',
    northStarMetricValue: data?.northStarMetricValue ?? '',
    arr: data?.arr ?? '',
    location: data?.location ?? '',
    websiteUrl: data?.websiteUrl ?? '',
    foundingDate: data?.foundingDate?.split('T')[0] ?? '',
    description: data?.description ?? '',
  })

  const form = useForm({
    defaultValues: getFormValues(startup),
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: getFormValues is a stable factory, startup?.id guards re-runs
  useEffect(() => {
    if (!startup) return
    setEditedTags(startup.tags ?? [])
    setLogoUrl(startup.logoUrl ?? null)
    setCoverImageUrl(startup.coverImageUrl ?? null)
    form.reset(getFormValues(startup))
  }, [startup?.id, form.reset, startup])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !startup) {
    return (
      <div className="flex items-center justify-center py-20">
        <ErrorBanner message={error?.message || 'Failed to load startup'} />
      </div>
    )
  }

  const handleUnlinkDocument = (documentId: string) => {
    setDocumentsToUnlink((prev) => [...prev, documentId])
  }

  const handleRemovePendingDocument = (documentId: string) => {
    setPendingDocuments((prev) => prev.filter((d) => d.documentId !== documentId))
  }

  const handlePreviewDocument = (doc: {
    documentId: string
    document: { fileName: string; mimeType: string | null }
  }) => {
    setPreviewDocument({
      id: doc.documentId,
      fileName: doc.document.fileName,
      mimeType: doc.document.mimeType,
    })
  }

  const handleUploadComplete = (documentId: string, file: File) => {
    setPendingDocuments((prev) => [...prev, { documentId, fileName: file.name, label: file.name }])
  }

  const handlePendingDocumentLabelChange = (documentId: string, label: string) => {
    setPendingDocuments((prev) =>
      prev.map((d) => (d.documentId === documentId ? { ...d, label } : d))
    )
  }

  const handleDocumentLabelChange = (documentId: string, label: string) => {
    setDocumentLabelEdits((prev) => {
      const existing = prev.find((e) => e.documentId === documentId)
      if (existing) {
        return prev.map((e) => (e.documentId === documentId ? { ...e, label } : e))
      }
      return [...prev, { documentId, label }]
    })
  }

  const getDocumentLabel = (documentId: string, originalLabel: string | null) => {
    const edit = documentLabelEdits.find((e) => e.documentId === documentId)
    return edit ? edit.label : (originalLabel ?? '')
  }

  const handleCancel = () => {
    form.reset(getFormValues(startup))
    setPendingDocuments([])
    setDocumentLabelEdits([])
    setDocumentsToUnlink([])
    setEditedTags(startup.tags ?? [])
    setLogoUrl(startup.logoUrl ?? null)
    setCoverImageUrl(startup.coverImageUrl ?? null)
    setNewTagInput('')
    onExitEdit()
  }

  const handleSave = async () => {
    const values = form.state.values
    updateStartupMut.mutate(
      {
        path: { id: startup.id },
        body: {
          name: values.name,
          categoryId: values.categoryId || null,
          productStage: (values.productStage as ProductStage) || null,
          isFundraising: values.isFundraising,
          fundraisingAmount: values.isFundraising ? values.fundraisingAmount || null : null,
          fundraisingStage: values.isFundraising ? values.fundraisingStage || null : null,
          isStealth: values.isStealth,
          northStarMetricName: values.northStarMetricName || null,
          northStarMetricValue: values.northStarMetricValue || null,
          arr: values.arr || null,
          location: values.location || null,
          websiteUrl: values.websiteUrl || null,
          foundingDate: values.foundingDate || null,
          description: values.description || null,
          tags: editedTags,
          logoUrl: logoUrl,
          coverImageUrl: coverImageUrl,
        },
      },
      {
        onSuccess: async () => {
          for (const documentId of documentsToUnlink) {
            unlinkDocumentMut.mutate({ path: { startupId: startup.id, documentId } })
          }

          for (const doc of pendingDocuments) {
            linkDocumentMut.mutate({
              path: { startupId: startup.id },
              body: { documentId: doc.documentId, label: doc.label },
            })
          }

          for (const edit of documentLabelEdits) {
            updateDocumentMut.mutate({
              path: { startupId: startup.id, documentId: edit.documentId },
              body: { label: edit.label },
            })
          }

          setPendingDocuments([])
          setDocumentLabelEdits([])
          setDocumentsToUnlink([])
          setEditedTags(startup.tags ?? [])
          setNewTagInput('')
          onExitEdit()
        },
      }
    )
  }

  return (
    <>
      <ProfileHeader
        coverImageUrl={coverImageUrl ?? startup.coverImageUrl}
        avatarUrl={logoUrl ?? startup.logoUrl}
        name={startup.name}
        subtitle={startup.category?.name}
        isEditMode
        onCoverImageChange={(file) => coverUpload.upload(file)}
        onAvatarChange={(file) => logoUpload.upload(file)}
        isCoverUploading={coverUpload.isUploading}
        isAvatarUploading={logoUpload.isUploading}
        badges={
          startup.isStealth ? (
            <Badge variant="secondary" size="md" className="gap-1">
              <EyeOff className="size-3" />
              Stealth
            </Badge>
          ) : undefined
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              <X className="size-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={updateStartupMut.isPending}>
              <Check className="size-4" />
              {updateStartupMut.isPending ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="space-y-4">
          <form.Field name="isFundraising">
            {(field) => (
              <ToggleField
                label="Actively Raising"
                description="Show that you're currently seeking investment"
                checked={field.state.value}
                onChange={(checked) => field.handleChange(checked)}
              >
                {field.state.value && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <form.Field name="fundraisingStage">
                      {(stageField) => (
                        <FormField label="Round">
                          <Select
                            value={stageField.state.value}
                            onValueChange={(value) => stageField.handleChange(value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select round type" />
                            </SelectTrigger>
                            <SelectContent>
                              {raiseStages.map((stage) => (
                                <SelectItem key={stage.value} value={stage.value}>
                                  {stage.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormField>
                      )}
                    </form.Field>
                    <form.Field name="fundraisingAmount">
                      {(amountField) => (
                        <FormField label="Amount">
                          <TextInput
                            value={amountField.state.value}
                            onChange={(e) => amountField.handleChange(e.target.value)}
                            placeholder="e.g., 2000000"
                          />
                        </FormField>
                      )}
                    </form.Field>
                  </div>
                )}
              </ToggleField>
            )}
          </form.Field>
        </div>

        <ProfileSection title="Basic Info">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <form.Field name="name">
                {(field) => (
                  <FormField label="Startup Name">
                    <TextInput
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Startup name"
                    />
                  </FormField>
                )}
              </form.Field>
              <form.Field name="categoryId">
                {(field) => (
                  <FormField label="Category">
                    <Select
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                )}
              </form.Field>
              <form.Field name="location">
                {(field) => (
                  <FormField label="Location">
                    <TextInput
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="e.g., San Francisco, CA"
                    />
                  </FormField>
                )}
              </form.Field>
              <form.Field name="websiteUrl">
                {(field) => (
                  <FormField label="Website">
                    <TextInput
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="https://example.com"
                    />
                  </FormField>
                )}
              </form.Field>
              <form.Field name="foundingDate">
                {(field) => (
                  <FormField label="Founding Date">
                    <TextInput
                      type="date"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            <form.Field name="description">
              {(field) => (
                <FormField label="Description">
                  <Textarea
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Describe what your startup does..."
                    rows={4}
                  />
                </FormField>
              )}
            </form.Field>
          </div>
        </ProfileSection>

        <ProfileSection title="Key Metrics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <form.Field name="northStarMetricName">
              {(field) => (
                <FormField label="North Star Metric Name">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="e.g., Monthly Active Users"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="northStarMetricValue">
              {(field) => (
                <FormField label="North Star Metric Value">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="e.g., 2,450"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="arr">
              {(field) => (
                <FormField label="ARR">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="e.g., 120000"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="productStage">
              {(field) => (
                <FormField label="Product Stage">
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value as ProductStage)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {productStages.map((stage) => (
                        <SelectItem key={stage.value} value={stage.value}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}
            </form.Field>
          </div>
        </ProfileSection>

        <ProfileSection title="Team">
          <div className="space-y-3">
            {members.map((member) => (
              <MemberCard
                key={member.id}
                member={{
                  id: member.id,
                  name: member.name ?? '',
                  email: member.email ?? '',
                  position: member.position ?? '',
                  imageUrl: member.imageUrl,
                  linkedinUrl: member.linkedinUrl,
                  isLinked: !!member.userId,
                }}
                onUpdate={(data) =>
                  updateMemberMutation.mutate({
                    path: { startupId: startup.id, memberId: member.id },
                    body: data,
                  })
                }
                onDelete={() =>
                  deleteMemberMutation.mutate({
                    path: { startupId: startup.id, memberId: member.id },
                  })
                }
                isUpdating={updateMemberMutation.isPending || deleteMemberMutation.isPending}
              />
            ))}

            <AddMemberForm
              onAdd={(data) =>
                createMemberMutation.mutate({
                  path: { startupId: startup.id },
                  body: data,
                })
              }
              isAdding={createMemberMutation.isPending}
            />
          </div>
        </ProfileSection>

        <ProfileSection title="Documents">
          <div className="space-y-4">
            <div className="space-y-3">
              {documents
                .filter((doc) => !documentsToUnlink.includes(doc.documentId))
                .map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 rounded-xl border border-border bg-input/50 space-y-4"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="size-8 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.document.fileName}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.document.fileSize && formatFileSize(doc.document.fileSize)}
                          {doc.document.fileSize && doc.document.createdAt && ' · '}
                          {doc.document.createdAt &&
                            formatDate(doc.document.createdAt, { month: 'short' })}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewDocument(doc)}
                      >
                        <Eye className="size-4" />
                        Preview
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleUnlinkDocument(doc.documentId)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
                        title="Remove document"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>

                    <FormField label="Label">
                      <TextInput
                        value={getDocumentLabel(doc.documentId, doc.label)}
                        onChange={(e) => handleDocumentLabelChange(doc.documentId, e.target.value)}
                        placeholder="e.g., Pitch Deck Q4 2024"
                      />
                    </FormField>
                  </div>
                ))}

              {pendingDocuments.map((doc) => (
                <div
                  key={doc.documentId}
                  className="p-4 rounded-xl border border-dashed border-[#f97316]/50 bg-[#f97316]/5 space-y-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center size-10 rounded-lg bg-[#f97316]/20 shrink-0">
                      <FileText className="size-5 text-[#f97316]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{doc.fileName}</p>
                      <p className="text-xs text-muted-foreground">Pending upload</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemovePendingDocument(doc.documentId)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
                      title="Remove document"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>

                  <FormField label="Label">
                    <TextInput
                      value={doc.label}
                      onChange={(e) =>
                        handlePendingDocumentLabelChange(doc.documentId, e.target.value)
                      }
                      placeholder="e.g., Pitch Deck Q4 2024"
                    />
                  </FormField>
                </div>
              ))}
            </div>

            <DocumentUpload onUploadComplete={handleUploadComplete} />
          </div>
        </ProfileSection>

        <ProfileSection title="Tags">
          <div className="space-y-3">
            {editedTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {editedTags.map((tag) => (
                  <Badge key={tag} variant="secondary" size="md" className="pr-1.5 gap-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setEditedTags((prev) => prev.filter((t) => t !== tag))}
                      className="ml-1 p-0.5 rounded hover:bg-white/10 transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            <div className="p-4 rounded-xl border border-dashed border-border bg-input/30">
              <div className="flex items-center gap-2">
                <TextInput
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  placeholder="Enter tag name"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTagInput.trim()) {
                      e.preventDefault()
                      const tag = newTagInput.trim()
                      if (!editedTags.includes(tag)) {
                        setEditedTags((prev) => [...prev, tag])
                      }
                      setNewTagInput('')
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!newTagInput.trim()}
                  onClick={() => {
                    const tag = newTagInput.trim()
                    if (tag && !editedTags.includes(tag)) {
                      setEditedTags((prev) => [...prev, tag])
                    }
                    setNewTagInput('')
                  }}
                >
                  <Plus className="size-4" />
                  Add
                </Button>
              </div>
            </div>
          </div>
        </ProfileSection>

        <ProfileSection title="Stealth Mode">
          <form.Field name="isStealth">
            {(field) => (
              <ToggleField
                label="Stealth Mode"
                description="Hide your startup from investors"
                checked={field.state.value}
                onChange={(checked) => field.handleChange(checked)}
              />
            )}
          </form.Field>
        </ProfileSection>
      </div>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => !open && setPreviewDocument(null)}
        />
      )}
    </>
  )
}
